<template>
    <div>
        <div class="landing__main p--0">
            <div class="landing__main__bg relative bg-main">
                <LandingHeader page-type="home" />
                
                <div class="landing__main__bg__inner landing-px-2">
                    <div id="hero" class="landing__hero__wrap landing__section">
                        <div class="landing__hero__wrap__text landing__hero__fadeFirst">
                            <h1 class="landing__hero__title text--heading text--7xl">
                                Pricing plans
                            </h1>
                            <p class="landing__hero__text">
                                Ziner offers a wide range of free and paid features + integrations designed to boost productivity.
                            </p>
                        </div>

                        <div class="landing__hero__fadeIn">
                            <div class="landing__hero__btns">
                                <BaseButton 
                                    label="Start with free plan" 
                                    size="md"
                                    shadow
                                    @submit="handleGetStarted('free')"
                                />
                            </div>
                        </div>
                    </div>

                    <div v-if="showCurrencyDropdown" class="flex justify-content-end mb--25 landing__triple__cards mt--0">
                        <CustomDropdown 
                            id="statusDropdown" 
                            v-model="selectedCurrency" 
                            :searchable="false"
                            btn-classes="text--upper text--normal" 
                            list-item-class="text--upper" 
                            :options="['USD', 'NGN']"
                            list-type="currencies" 
                            @select="v => selectedCurrency = v" 
                        />
                    </div>

                    <div class="landing__triple__cards mt--30">
                        <Plans 
                            :currency="currencyToUse"
                            type="pricing"
                            @set-plan="handleGetStarted" 
                        />
                    </div>

                    <div class="landing__triple__cards comparison-section-wrap">
                        <div class="comparison-section">
                            <div class="comparison-header">
                                <h2 class="comparison-title">
                                    Compare plans
                                </h2>
                                <div v-if="windowWidth < 768" class="flex justify-content-end mb--15">
                                    <CustomDropdown 
                                        id="statusDropdown" 
                                        v-model="selectedPlan" 
                                        :searchable="false"
                                        btn-classes="text--capitalize text--normal" 
                                        list-item-class="text--capitalize" 
                                        :options="['free', 'standard', 'plus']"
                                        list-type="currencies" 
                                        @select="setPlan" 
                                    />
                                </div>
                            </div>

                            <template v-if="windowWidth > 768">
                                <div class="plans-overview desktop">
                                    <div class="plan-overview" />
                                    <div v-for="item in pricingInfoData" :key="item.plan" class="plan-overview" :class="{ 'popular': item.plan === 'standard' }">
                                        <div v-if="item.plan === 'standard'" class="popular-label">
                                            Most Popular
                                        </div>
                                        <h3 class="plan-title text--capitalize">
                                            {{ item.plan }}
                                        </h3>
                                        <div class="plan-pricing">
                                            <div>
                                                <span class="currency">{{ currencySymbol }}</span>
                                                <span class="price-amount" data-monthly="19" data-annual="15">{{ getPlanAmt(item.plan) }}</span>
                                            </div>
                                            <div class="price-details">
                                                <span class="price-period">per/month</span>
                                                <!-- <div class="annual-price">
                                                    <span class="annual-amount" data-monthly="$228" data-annual="$180">$228</span> billed yearly
                                                </div> -->
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <template v-else>
                                <div class="plans-overview mobile">
                                    <div v-for="item in pricingInfoData" :key="item.plan" class="plan-overview" :class="{ 'selected': item.plan === selectedPlan.plan }">
                                        <h3 class="plan-title text--capitalize">
                                            {{ selectedPlan.plan }}
                                        </h3>
                                        <div class="plan-pricing">
                                            <div>
                                                <span class="currency">{{ currencySymbol }}</span>
                                                <span class="price-amount" data-monthly="19" data-annual="15">{{ selectedPlan.amount }}</span>
                                            </div>
                                            <div class="price-details">
                                                <span class="price-period">per/month</span>
                                                <div class="annual-price">
                                                    Billed monthly
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>
    
                            <div class="features-comparison">
                                <template v-if="windowWidth > 768">
                                    <div v-for="item in groupByKeys" :key="item" class="features-section">
                                        <h4 class="section-title">
                                            {{ item }}
                                        </h4>
                            
                                        <div v-for="feat in groupedListByType[item]" :key="feat.name" class="feature-row">
                                            <div class="feature-info">
                                                <span class="feature-name">{{ feat.name }}</span>
                                                <!-- <span class="feature-tooltip">ℹ️</span> -->
                                            </div>
                                            <div class="feature-values">
                                                <span v-for="limit in feat.limits" :key="`key-${limit}`" :class="[getClass(limit)]">{{ getText(limit) }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <template v-else>
                                    <div v-for="item in groupByKeys" :key="item.name" class="features-section">
                                        <h4 class="section-title">
                                            {{ item }}
                                        </h4>
                            
                                        <div v-for="feat in groupedListByType[item]" :key="feat.name" class="feature-row">
                                            <div class="feature-info">
                                                <IconSvg :name="getIconName(feat?.limits[selectedPlan?.index])" />
                                                <span class="feature-name">{{ feat.name }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>

                <LandingFooter id="footer" class="mt-3" />
            </div>
            <div />
        </div>
    </div>
</template>

<script>
import { BaseButton, Plans, IconSvg, CustomDropdown } from "@/components/ui/index";
import { pricingInfoData } from "@/utils/dummy";
import LandingFooter from "@/components/home/LandingFooter.vue";
import LandingHeader from "@/components/home/LandingHeader.vue";
import { supportedCurrenciesMap } from "@/plugins/countries";
import benefits from "@/assets/js/benefits.json";
import { groupList } from "@/utils/helpers";

export default {
    name: "PricingPage",

    components: {
        BaseButton, 
        LandingFooter,
        LandingHeader,
        Plans,
        IconSvg,
        CustomDropdown,
    },
    
    data() {
        return {
            pricingInfoData,
            supportedCurrenciesMap,
            benefits: benefits?.data,
            windowWidth: window.innerWidth,
            windowHeight: window.innerHeight,
            selectedPlan: {
                plan: "free",
                amount: this.getPlanAmt("free"),
                index: 0,
            },
            selectedCurrency: "",
            initialCurrencyList: Object.values(this.$currencies_obj),
        };
    },

    computed: {
        currency() {
            return this.$store.getters["settings/general"]?.currency;
        },

        systemPlans() {
            return this.$store.getters["config/systemPlans"];
        },

        currencyToUse() {
            return this.selectedCurrency || this.currency;
        },

        currencySymbol() {      
            const currency = this.selectedCurrency || this.currency; 
            return this.$supported_currencies_map[this.currencyToUse?.toLowerCase()]?.symbol;
        },

        groupedListByType() {
            const list = this.groupList(this.benefits, (item) => {
                const key = item.benefitType;
                return key;
            });

            return Object.fromEntries(list);
        },

        groupByKeys() {
            return Object.keys(this.groupedListByType);
        },

        showCurrencyDropdown() {
            return this.currency === "NGN";
        },
    },
     
    async mounted() {
        await this.fetchSystemPlans();
        await this.fetchGeoLocationData();
        window.addEventListener("resize", this.onResize);
    },

    beforeDestroy() {
        window.removeEventListener("resize", this.onResize); 
    },

    methods: {
        groupList,

        getText(val) {
            if (val === "-") return "—";
            if (val === "") return "✓";
            return val;
        },

        getTextMobile(val) {
            if (val === "-") return "";
            if (val === "") return "";
            return val;
        },

        getClass(val) {
            if (val === "-") return "feature-unavailable";
            if (val === "") return "feature-check";
            return "feature-name";
        },

        getIconName(val) {
            if (val === "-") return "x-circle";
            return "check-successful";
        },

        onResize() {
            this.windowWidth = window.innerWidth;
            this.windowHeight = window.innerHeight;
        },

        handleGetStarted(accountPlan) {
            if (!accountPlan || typeof accountPlan !== "string") return;
            
            this.$store.dispatch("app/setSelectedPlan", accountPlan?.toLowerCase());
            this.$router.push("/auth/signup");
        },

        computePricingAction(accountPlan) {
            if (accountPlan === "free") {
                return this.$router.push("/auth/signup");
            }

            localStorage.setItem("z_pricing_plan", accountPlan);
            localStorage.setItem("z_pricing_plan_currency", "NGN");
            localStorage.setItem("z_pricing_plan_active", "NGN");
            return this.$router.push("/auth/signup");
        },

        async fetchGeoLocationData() {
            try {                
                const storedCurrency = localStorage.getItem("z_ip_currency");
                
                if (storedCurrency === this.currency) return; // no need because currency in vuex store === local storage currency

                const response = await fetch("https://ipapi.co/8.8.8.8/json/", {
                    method: "GET",
                });

                if (!response.ok) {
                    // Improved error message to include response status
                    throw new Error(`An error occured failed with status ${response.status}: ${await response.text()}`);
                }

                const jsonData = await response.json();
                const currency = jsonData?.currency || "USD";

                this.$store.dispatch("settings/setCurrency", { currency });
                localStorage.setItem("z_ip_currency", currency);
                this.selectedCurrency = currency;
            } catch (error) {
                console.log("fetchGeoLocationDataError", error);
            }
        },

        async fetchSystemPlans() {
            try {   
                await this.$store.dispatch("config/fetchSystemPlans");
            } catch (error) {
                console.log("fetch system plans", error);
            }
        },

        getPlanAmt(plan = "free") {    
            if (!this.systemPlans) return 0.00;
                    
            const yie = this.$format_amount(this.systemPlans[plan?.toLowerCase()]?.amount[this?.currencyToUse?.toUpperCase()]?.monthlyAmount, 2);            
            return this.$format_amount(this.systemPlans[plan?.toLowerCase()]?.amount[this?.currencyToUse?.toUpperCase()]?.monthlyAmount, 2) || 0;
        },

        setPlan(val) {
            const index = val === "free" ? 0 : val === "standard" ? 1 : 2;

            this.selectedPlan = {
                plan: val,
                amount: this.getPlanAmt(val),
                index,
            };
        },
    },
};
</script>